import * as Sentry from "@sentry/react";
import { logger } from "./logger";

/**
 * Recovery from a stale-deploy chunk load failure.
 *
 * A tab that was open across a deploy still holds the previous index.html, so
 * its next lazy route asks for /assets/<oldhash>.js. Normally the server still
 * has it -- the deploy overlays new files instead of wiping the directory. But
 * if retention has finally aged that file out, nginx 404s it, the dynamic import
 * rejects, React.lazy caches that rejection permanently, and the root
 * ErrorBoundary renders Error500 ("500 Greska na serveru") even though nothing
 * ever returned a 500.
 *
 * The only genuine reset is a fresh document: index.html is served no-cache, so
 * a reload always comes back naming the current hashes.
 */

const RELOAD_KEY = "polaris:chunk-reload-at";
const COOLDOWN_MS = 30_000;

let reloadPending = false;

/** True once a recovery reload has been triggered and the document is on its way out. */
export function isChunkReloadPending(): boolean {
  return reloadPending;
}

const CHUNK_ERROR_PATTERNS = [
  "failed to fetch dynamically imported module", // Chrome / Edge
  "error loading dynamically imported module", // Firefox
  "importing a module script failed", // Safari
  "unable to preload css", // Vite's preload helper
];

export function isChunkLoadError(error: unknown): boolean {
  const message = (
    error instanceof Error ? error.message : String(error ?? "")
  ).toLowerCase();
  return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

/** Trigger a one-shot recovery reload, unless we already tried very recently. */
function requestChunkReload(reason: string, error: unknown): void {
  if (reloadPending) return;

  // Reloading while offline replaces the app with the browser's offline page.
  if (navigator.onLine === false) return;

  let lastAttempt = 0;
  try {
    lastAttempt = Number(sessionStorage.getItem(RELOAD_KEY)) || 0;
  } catch {
    // Private mode or storage blocked: fall through and allow one attempt.
  }

  if (Date.now() - lastAttempt < COOLDOWN_MS) {
    // We already reloaded for this and it happened again, so the asset is
    // genuinely gone or the new build is broken. Reloading again would loop.
    // Let it surface: Error500 plus a Sentry report is the right outcome.
    return;
  }

  try {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // Ignore: the cooldown degrades to "one reload per page load", which the
    // reloadPending flag already guarantees.
  }

  reloadPending = true;

  Sentry.captureException(
    error instanceof Error ? error : new Error(String(error)),
    {
      tags: { errorType: "chunk_load" },
      extra: { reason, url: window.location.href },
    },
  );
  logger.warn("Reloading to recover from a stale deploy", {
    reason,
    url: window.location.href,
  });
  void logger.flush();

  window.location.reload();
}

/**
 * Listen for chunk load failures and reload once to pick up the current build.
 *
 * Vite wraps every dynamic import in its preload helper, which dispatches this
 * event when the module fetch fails -- verified in the built bundle, where each
 * route is emitted as `__vitePreload(() => import("./<hash>.js"), deps)`. So
 * this one listener covers all 13 lazy routes without touching routes.tsx.
 *
 * It deliberately does NOT call preventDefault(). Vite's helper is:
 *
 *   function handlePreloadError(err) { ...; if (!e.defaultPrevented) throw err }
 *   return promise.then(res => { ...; return baseModule().catch(handlePreloadError) })
 *
 * so once the event is defaultPrevented the handler returns undefined and the
 * import RESOLVES with undefined. React then reads `.default` off undefined and
 * throws a TypeError into the root ErrorBoundary -- which flashes the 500 screen
 * for the entire reload round-trip, the exact thing we are trying to avoid.
 *
 * Letting it throw means React.lazy rejects as normal and the error reaches the
 * root ErrorBoundary, which checks isChunkReloadPending() and shows a skeleton
 * instead of the error page while the new document loads.
 */
export function initChunkReload(): void {
  window.addEventListener("vite:preloadError", (event) => {
    requestChunkReload("vite:preloadError", event.payload);
  });
}
